import type { CompleteOauthParams, OauthLoginFailure } from '../../../../auth/oauth/auth-sync.js';

/**
 * Asks the backend what a social sign-in should do, before Turnkey is given
 * the token.
 *
 * Turnkey's `completeOauth` signs in when it recognises the identity and
 * creates a sub-organization and a fresh account when it does not, which is
 * how one person ends up owning several Morfi accounts. Callers run this
 * first and stop when the answer says this sign-in would be that second
 * account.
 */

/** What the sign-in must do next. Mirrors `POST /v1/auth/resolve-login`. */
export interface LoginResolution {
  readonly decision: 'proceed' | 'link_required';
  /** Set for `link_required` — the methods that do reach the account. */
  readonly methods: readonly string[];
}

/** How the account's own sign-in methods are named to the person using them. */
const METHOD_LABELS: Readonly<Record<string, string>> = {
  google: 'Google',
  x: 'X',
  apple: 'Apple',
  otp: 'email',
  email: 'email',
};

/** "Google or email" — deduplicated, because otp and email are one method. */
export function describeLoginMethods(methods: readonly string[]): string {
  return methods
    .map((method) => METHOD_LABELS[method] ?? method)
    .filter((label, index, all) => all.indexOf(label) === index)
    .join(' or ');
}

/**
 * Resolve the sign-in. Throws when the backend cannot answer: the caller must
 * then abort rather than fall through to Turnkey, because falling through is
 * exactly what creates the duplicate. A sign-in could not be completed during
 * such an outage anyway — the Morfi session is minted by the same backend.
 */
export async function resolveLogin(params: {
  readonly oidcToken: string;
  readonly provider: string;
}): Promise<LoginResolution> {
  const res = await fetch('/v1/auth/resolve-login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ oidcToken: params.oidcToken, provider: params.provider }),
  });
  if (!res.ok) throw new Error(`POST /v1/auth/resolve-login failed: ${String(res.status)}`);
  const json = (await res.json()) as { data?: Partial<LoginResolution> };
  const decision = json.data?.decision;
  if (decision !== 'proceed' && decision !== 'link_required') {
    throw new Error('POST /v1/auth/resolve-login returned no decision');
  }
  return { decision, methods: json.data?.methods ?? [] };
}

/**
 * Run the sign-in only when it would not clone an existing account.
 *
 * `completeOauth` signs in when Turnkey recognises the identity and creates a
 * sub-organization and a new account when it does not, so the second outcome
 * is the one worth guarding. A refusal names the methods that do reach the
 * account, and the backend only ever issues one when such a method exists —
 * nobody is left unable to sign in, export their wallets and clean up.
 *
 * A pre-flight that cannot answer aborts the sign-in rather than falling
 * through, because falling through is the duplicate. Nothing is lost: the
 * Morfi session is minted by the same backend that just failed to answer.
 */
export async function runResolvedLogin(input: {
  oidcToken: string;
  publicKey: string;
  providerName: string;
  runCompleteOauth: (params: CompleteOauthParams) => Promise<void>;
  signalOauthLoginFailed: (failure?: OauthLoginFailure) => void;
}): Promise<void> {
  const resolution = await resolveLogin({
    oidcToken: input.oidcToken,
    provider: input.providerName,
  });

  if (resolution.decision === 'link_required') {
    input.signalOauthLoginFailed({ reason: 'link-required', methods: resolution.methods });
    return;
  }

  await input.runCompleteOauth({
    oidcToken: input.oidcToken,
    publicKey: input.publicKey,
    providerName: input.providerName,
  });
}

/** The copy an app supplies for the two failure shapes. */
export interface LoginFailureCopy {
  readonly signedUpWithProvider?: (methods: string) => string;
  readonly error?: string;
}

/**
 * The message to show when a sign-in stops before a Morfi session exists.
 *
 * A `link-required` stop is the pre-flight refusing to clone an account, so it
 * must name the methods that do reach it. Both platforms render the same
 * decision, so it lives here rather than being written twice.
 */
export function loginFailureMessage(
  failure: OauthLoginFailure,
  copy: LoginFailureCopy | undefined,
): string {
  if (failure.reason === 'link-required' && failure.methods.length > 0) {
    const labels = describeLoginMethods(failure.methods);
    return copy?.signedUpWithProvider?.(labels) ?? `Please sign in with ${labels}`;
  }
  return copy?.error ?? 'An error occurred during sign in';
}
